import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import {
  Button,
  Drawer,
  Form,
  Input,
  Popconfirm,
  Select,
  Spin,
  Table,
} from 'antd';
import { API } from '@services/api.service';
import AlertBox from '@/components/alert-box';
import { UploadMultipleFile } from '@/components/upload-multiple-file';
import { UploadedAttachmentsList } from '@/components/uploaded-attachments-list';
import { useNotify } from '@/hooks';
import {
  TRAVEL_ABROAD_CURRENCIES,
  TRAVEL_DOMESTIC_CURRENCY,
  TRAVEL_INVOICE_COST_KINDS,
  TRAVEL_INVOICE_TYPES,
} from './constants';
import TravelReceiptsReviewModal from './TravelReceiptsReviewModal';

const TYPE_ORDER = TRAVEL_INVOICE_TYPES;

const EMPTY_INVOICE = {
  travelInvoiceCurrency: TRAVEL_DOMESTIC_CURRENCY,
  travelInvoiceNumber: '',
  travelInvoiceType: TRAVEL_INVOICE_TYPES[0],
  travelInvoiceCostKind: '',
  travelInvoiceDescription: '',
  travelInvoiceAmount: '',
  isTravelInvoicePaidByCard: false,
};

/**
 * Third wizard step — the receipts backing the settlement.
 *
 * Receipts are grouped per currency and, within a currency, per type with a
 * subtotal row — the same layout the travel order document prints, so what the
 * user checks here is what comes out on paper.
 */
export default function TravelInvoicesStep({
  requestId,
  travelOrder,
  travelInvoices = [],
  travelAttachments = [],
  employeeId,
  disabled,
  isPending,
  onSubmit,
  onAttachmentsChange,
}) {
  const { t } = useTranslation(['officialTravel', 'messages']);
  const notification = useNotify();
  const [form] = Form.useForm();
  const drawerCurrency = Form.useWatch('travelInvoiceCurrency', form);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editedIndex, setEditedIndex] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isReading, setIsReading] = useState(false);
  const [readResult, setReadResult] = useState(null);
  // The dropzone reports every file it still lists on each change, so a file
  // already sent would go up again with the next one. Remounting it after an
  // upload empties it; the set covers changes fired while one is in flight.
  const sentFilesRef = useRef(new WeakSet());
  const [dropzoneKey, setDropzoneKey] = useState(0);

  const abroadCurrency =
    travelOrder?.dailyWageForAbroadCurrency || TRAVEL_ABROAD_CURRENCIES[0];

  // A receipt can be in any currency — a taxi in zloty on a trip paid in
  // euros. Dinars and the per diem currency come first as the usual picks;
  // every other one is converted at its own rate on the summary step.
  const currencyOptions = useMemo(
    () =>
      [
        ...new Set([
          TRAVEL_DOMESTIC_CURRENCY,
          abroadCurrency,
          ...TRAVEL_ABROAD_CURRENCIES,
        ]),
      ].map((value) => ({ value, label: value })),
    [abroadCurrency]
  );

  const typeOptions = useMemo(
    () =>
      TRAVEL_INVOICE_TYPES.map((value) => ({
        value,
        label: t(`options.invoiceType.${value}`, { ns: 'officialTravel' }),
      })),
    [t]
  );

  const costKindOptions = useMemo(
    () =>
      TRAVEL_INVOICE_COST_KINDS.map((value) => ({
        value,
        label: t(`options.costKind.${value || 'Other'}`, {
          ns: 'officialTravel',
        }),
      })),
    [t]
  );

  const cardOptions = useMemo(
    () => [
      { value: true, label: t('options.yes', { ns: 'officialTravel' }) },
      { value: false, label: t('options.no', { ns: 'officialTravel' }) },
    ],
    [t]
  );

  const formatAmount = (value) =>
    new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(value) || 0);

  // Rows per currency: receipts in type order, each type closed by a subtotal.
  const tablesByCurrency = useMemo(() => {
    const byCurrency = travelInvoices.reduce((acc, invoice, index) => {
      const currency = invoice.travelInvoiceCurrency || TRAVEL_DOMESTIC_CURRENCY;
      if (!acc[currency]) acc[currency] = [];
      // The source index travels with the row so edit and delete can address
      // the right receipt after the grouping has reordered everything.
      acc[currency].push({ ...invoice, sourceIndex: index });
      return acc;
    }, {});

    return Object.entries(byCurrency).map(([currency, invoices]) => {
      const rows = [];
      let ordinal = 1;

      TYPE_ORDER.forEach((type) => {
        const ofType = invoices.filter(
          (invoice) => invoice.travelInvoiceType === type
        );
        if (!ofType.length) return;

        let subtotal = 0;

        ofType.forEach((invoice) => {
          subtotal += Number(invoice.travelInvoiceAmount) || 0;
          rows.push({
            ...invoice,
            rowKey: `invoice-${invoice.sourceIndex}`,
            index: ordinal++,
            isSummary: false,
          });
        });

        rows.push({
          rowKey: `summary-${currency}-${type}`,
          index: '',
          travelInvoiceNumber: t('invoices.costSummary', {
            ns: 'officialTravel',
          }),
          travelInvoiceType: type,
          travelInvoiceDescription: '',
          travelInvoiceAmount: subtotal,
          travelInvoiceCurrency: currency,
          isTravelInvoicePaidByCard: '',
          isSummary: true,
        });
      });

      return { currency, rows };
    });
  }, [travelInvoices, t]);

  const columns = useMemo(() => {
    const base = [
      {
        title: t('invoices.columns.index', { ns: 'officialTravel' }),
        dataIndex: 'index',
        key: 'index',
        width: 70,
      },
      {
        title: t('invoices.travelInvoiceNumber', { ns: 'officialTravel' }),
        dataIndex: 'travelInvoiceNumber',
        key: 'travelInvoiceNumber',
      },
      {
        title: t('invoices.travelInvoiceType', { ns: 'officialTravel' }),
        dataIndex: 'travelInvoiceType',
        key: 'travelInvoiceType',
        render: (value, record) =>
          [
            value && t(`options.invoiceType.${value}`, { ns: 'officialTravel' }),
            !record.isSummary &&
              record.travelInvoiceCostKind &&
              t(`options.costKind.${record.travelInvoiceCostKind}`, {
                ns: 'officialTravel',
              }),
          ]
            .filter(Boolean)
            .join(' · '),
      },
      {
        title: t('invoices.travelInvoiceDescription', { ns: 'officialTravel' }),
        dataIndex: 'travelInvoiceDescription',
        key: 'travelInvoiceDescription',
      },
      {
        title: t('invoices.travelInvoiceAmount', { ns: 'officialTravel' }),
        dataIndex: 'travelInvoiceAmount',
        key: 'travelInvoiceAmount',
        align: 'right',
        render: (value) => formatAmount(value),
      },
      {
        title: t('invoices.isTravelInvoicePaidByCard', {
          ns: 'officialTravel',
        }),
        dataIndex: 'isTravelInvoicePaidByCard',
        key: 'isTravelInvoicePaidByCard',
        render: (value, record) => {
          if (record.isSummary) return '';
          return value
            ? t('options.yes', { ns: 'officialTravel' })
            : t('options.no', { ns: 'officialTravel' });
        },
      },
    ];

    if (disabled) return base;

    return [
      ...base,
      {
        title: '',
        key: 'actions',
        width: 90,
        render: (_, record) =>
          record.isSummary ? null : (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => openDrawer(record.sourceIndex)}
              >
                <EditOutlined />
              </button>

              <Popconfirm
                placement="left"
                title={t('invoices.deleteConfirm', { ns: 'officialTravel' })}
                onConfirm={() => handleDelete(record.sourceIndex)}
              >
                <button type="button">
                  <DeleteOutlined />
                </button>
              </Popconfirm>
            </div>
          ),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, disabled, travelInvoices]);

  const openDrawer = (index = null) => {
    setEditedIndex(index);
    form.setFieldsValue(
      index === null ? EMPTY_INVOICE : { ...EMPTY_INVOICE, ...travelInvoices[index] }
    );
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditedIndex(null);
    form.setFieldsValue(EMPTY_INVOICE);
  };

  // The whole list is sent on every change, so add, edit and delete are one
  // and the same operation as far as the request is concerned.
  const handleDelete = (index) => {
    onSubmit(travelInvoices.filter((_, i) => i !== index));
  };

  const handleFinish = (values) => {
    const invoice = {
      ...values,
      // Only a domestic receipt books by cost kind.
      travelInvoiceCostKind:
        values.travelInvoiceCurrency === TRAVEL_DOMESTIC_CURRENCY
          ? values.travelInvoiceCostKind || ''
          : '',
      travelInvoiceAmount: Number(values.travelInvoiceAmount) || 0,
      isTravelInvoicePaidByCard: Boolean(values.isTravelInvoicePaidByCard),
    };

    const next =
      editedIndex === null
        ? [...travelInvoices, invoice]
        : travelInvoices.map((item, i) =>
            i === editedIndex ? { ...item, ...invoice } : item
          );

    onSubmit(next);
    closeDrawer();
  };

  // Receipts go to S3 through the platform's file endpoint; the request keeps
  // only the resulting File pointers, which is also what the document counts as
  // "Broj priloga uz obračun".
  const handleFilesSelected = async (files) => {
    const fresh = (files || []).filter(
      (file) => !sentFilesRef.current.has(file)
    );
    if (!fresh.length) return;
    fresh.forEach((file) => sentFilesRef.current.add(file));

    try {
      setIsUploading(true);

      const formData = new FormData();
      fresh.forEach((file) => formData.append('files', file));
      formData.append('employeeId', employeeId);

      const response = await API.files.uploadMultipleFiles(formData);
      const createdFiles = response?.data?.createdFiles || [];

      await onAttachmentsChange([
        ...travelAttachments,
        ...createdFiles.map((created) => ({
          file: created._id,
          key: created.key,
          title: created.title,
          type: created.type,
          location: created.location,
        })),
      ]);
      setDropzoneKey((key) => key + 1);

      // A new scan is read right away; the rows still go through review.
      if (createdFiles.length) {
        await handleReadReceipts(createdFiles.map((created) => created._id));
      }
    } catch (error) {
      notification.error({
        message: t('error', { ns: 'messages' }),
        description: error?.response?.data?.error || error?.message,
      });
    } finally {
      setIsUploading(false);
    }
  };

  // Gemini reads the attached files; the rows come back for review and only
  // the kept ones are saved, through the same update as a typed receipt.
  const handleReadReceipts = async (fileIds) => {
    if (!requestId) return;

    try {
      setIsReading(true);
      const result = await API.requests.readOfficialTravelReceipts(
        requestId,
        Array.isArray(fileIds) ? fileIds : undefined
      );
      setReadResult({
        receipts: result?.receipts || [],
        failedFiles: result?.failedFiles || [],
      });
    } catch (error) {
      notification.error({
        message: t('error', { ns: 'messages' }),
        description: error?.response?.data?.error || error?.message,
      });
    } finally {
      setIsReading(false);
    }
  };

  const handleAddReadReceipts = async (kept) => {
    try {
      await onSubmit([...travelInvoices, ...kept]);
      setReadResult(null);
    } catch {
      // The save already reported the failure; the review stays open.
    }
  };

  // The attachment list addresses rows by `_id`; a travel attachment is
  // identified by the file it points at.
  const attachmentsForList = travelAttachments.map((attachment) => ({
    ...attachment,
    _id: attachment.file || attachment.key,
  }));

  const handleAttachmentDelete = (fileId) => {
    onAttachmentsChange(
      travelAttachments.filter(
        (attachment) => (attachment.file || attachment.key) !== fileId
      )
    );
  };

  return (
    <div className="grid gap-4">
      {!disabled && (
        <div className="flex justify-end">
          <Button
            shape="round"
            className="btn-default btn-default--hover"
            onClick={() => openDrawer(null)}
          >
            {t('invoices.toggleCreate', { ns: 'officialTravel' })}
          </Button>
        </div>
      )}

      {tablesByCurrency.length === 0 ? (
        <AlertBox variant="info" className="mb-0">
          <p>{t('invoices.empty', { ns: 'officialTravel' })}</p>
        </AlertBox>
      ) : (
        tablesByCurrency.map(({ currency, rows }) => (
          <div key={currency} className="grid gap-2">
            <h4 className="font-semibold">
              {t('invoices.currencyTable', { ns: 'officialTravel', currency })}
            </h4>

            <Table
              dataSource={rows}
              columns={columns}
              pagination={false}
              bordered
              size="small"
              rowKey="rowKey"
              scroll={{ x: 'max-content' }}
              rowClassName={(record) =>
                record.isSummary ? 'bg-amber-50 font-semibold' : ''
              }
            />
          </div>
        ))
      )}

      <div className="grid gap-2 rounded-md border bg-zinc-50 p-4">
        <h4 className="font-semibold">
          {t('invoices.attachments', { ns: 'officialTravel' })}
        </h4>

        {!disabled && (
          <>
            <p className="text-sm text-zinc-600">
              {t('invoices.uploadHint', { ns: 'officialTravel' })}
            </p>

            <Spin spinning={isUploading}>
              <UploadMultipleFile
                key={dropzoneKey}
                onFilesChange={handleFilesSelected}
              />
            </Spin>
          </>
        )}

        {travelAttachments.length === 0 ? (
          <p className="text-sm text-zinc-600">
            {t('invoices.attachmentsEmpty', { ns: 'officialTravel' })}
          </p>
        ) : (
          <UploadedAttachmentsList
            attachments={attachmentsForList}
            onMarkFileForDeletion={
              disabled ? undefined : handleAttachmentDelete
            }
          />
        )}

        {!disabled && travelAttachments.length > 0 && (
          <div className="flex flex-wrap items-center justify-end gap-2">
            {isReading && (
              <span className="text-sm text-zinc-600">
                {t('invoices.read.reading', { ns: 'officialTravel' })}
              </span>
            )}
            <Button
              shape="round"
              className="btn-default btn-default--hover"
              loading={isReading}
              disabled={isReading || !requestId}
              onClick={() => handleReadReceipts()}
            >
              {t('invoices.read.button', { ns: 'officialTravel' })}
            </Button>
          </div>
        )}
      </div>

      <TravelReceiptsReviewModal
        open={Boolean(readResult)}
        receipts={readResult?.receipts}
        failedFiles={readResult?.failedFiles}
        isPending={isPending}
        onAdd={handleAddReadReceipts}
        onClose={() => setReadResult(null)}
      />

      <Drawer
        title={t('invoices.drawerTitle', { ns: 'officialTravel' })}
        open={drawerOpen}
        onClose={closeDrawer}
        width={420}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          autoComplete="off"
          initialValues={EMPTY_INVOICE}
          onFinish={handleFinish}
        >
          <Form.Item
            name="travelInvoiceCurrency"
            label={t('invoices.travelInvoiceCurrency', {
              ns: 'officialTravel',
            })}
          >
            <Select showSearch options={currencyOptions} />
          </Form.Item>

          <Form.Item
            name="travelInvoiceType"
            label={t('invoices.travelInvoiceType', { ns: 'officialTravel' })}
            rules={[
              {
                required: true,
                message: t('validation.invoiceTypeRequired', {
                  ns: 'officialTravel',
                }),
              },
            ]}
          >
            <Select options={typeOptions} />
          </Form.Item>

          {drawerCurrency === TRAVEL_DOMESTIC_CURRENCY && (
            <Form.Item
              name="travelInvoiceCostKind"
              label={t('invoices.travelInvoiceCostKind', {
                ns: 'officialTravel',
              })}
              tooltip={t('invoices.travelInvoiceCostKindHint', {
                ns: 'officialTravel',
              })}
            >
              <Select options={costKindOptions} />
            </Form.Item>
          )}

          <Form.Item
            name="travelInvoiceNumber"
            label={t('invoices.travelInvoiceNumber', { ns: 'officialTravel' })}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="travelInvoiceDescription"
            label={t('invoices.travelInvoiceDescription', {
              ns: 'officialTravel',
            })}
          >
            <Input.TextArea rows={2} />
          </Form.Item>

          <Form.Item
            name="travelInvoiceAmount"
            label={t('invoices.travelInvoiceAmount', { ns: 'officialTravel' })}
            rules={[
              {
                required: true,
                message: t('validation.amountRequired', {
                  ns: 'officialTravel',
                }),
              },
            ]}
          >
            <Input type="number" step="any" min={0} />
          </Form.Item>

          <Form.Item
            name="isTravelInvoicePaidByCard"
            label={t('invoices.isTravelInvoicePaidByCard', {
              ns: 'officialTravel',
            })}
          >
            <Select options={cardOptions} />
          </Form.Item>

          <div className="flex justify-end gap-2">
            <Button
              shape="round"
              className="btn-default btn-default--hover"
              onClick={closeDrawer}
              disabled={isPending}
            >
              {t('actions.cancel', { ns: 'officialTravel' })}
            </Button>

            <Button
              shape="round"
              type="primary"
              htmlType="submit"
              className="btn-primary"
              disabled={isPending}
              loading={isPending}
            >
              {t('actions.saveInvoice', { ns: 'officialTravel' })}
            </Button>
          </div>
        </Form>
      </Drawer>
    </div>
  );
}
